import { useRef, useState } from "react";
import { uploadItemPhoto } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { X, Image as ImageIcon, Loader2 } from "lucide-react";

interface Props {
  value: string[] | null | undefined;
  onChange: (next: string[]) => void;
  // Disable add button once max reached (defaults to 3).
  max?: number;
}

// 2026-05-19 hot-fix: 1-3 photos per item. Uploads to /api/uploads/item-photo,
// which returns a public Supabase Storage URL we store in inventory_items.photo_urls.
// PNG / JPEG / WebP only; 5MB cap on the server. Local preview thumbnails so the
// operator sees what they uploaded before saving the item.
export function ItemPhotosInput({ value, onChange, max = 3 }: Props) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const photos = value ?? [];
  const canAddMore = photos.length < max;

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= max) {
      toast({ title: `Maximum ${max} photos allowed.`, variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large.", description: "Photos must be 10MB or less.", variant: "destructive" });
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast({ title: "Unsupported file type.", description: "Use PNG, JPEG, or WebP.", variant: "destructive" });
      return;
    }
    setUploading(true);
    const result = await uploadItemPhoto(file);
    setUploading(false);
    if (!result.ok) {
      toast({ title: "Upload failed", description: result.message, variant: "destructive" });
      return;
    }
    onChange([...photos, result.url]);
  }

  function removeAt(idx: number) {
    onChange(photos.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {photos.length} / {max} photo{max === 1 ? "" : "s"}{photos.length === 0 && " — at least 1 required"}
        </p>
        {canAddMore && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Uploading…</>
            ) : (
              <><ImageIcon className="mr-2 h-3 w-3" />Add photo</>
            )}
          </Button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFileSelected}
        className="hidden"
      />
      {photos.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
          No photos yet. Click &ldquo;Add photo&rdquo; to upload (PNG / JPEG / WebP, max 10MB each).
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((url, i) => (
            <div key={url} className="relative group rounded-md overflow-hidden border bg-muted aspect-square">
              <img src={url} alt={`Item photo ${i + 1}`} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label={`Remove photo ${i + 1}`}
                className="absolute top-1 right-1 rounded-full bg-background/90 hover:bg-destructive hover:text-destructive-foreground p-1 shadow-sm transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
